import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../shared';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

/**
 * Compact, read-only panel that lists every credit note attached to a
 * given receipt OR booking. Designed to live right under a receipt card,
 * so accountants instantly see "this closed receipt has subsequent
 * adjustments — they have not modified the receipt itself".
 *
 * Props (use ONE of receiptId / bookingId):
 *  - receiptId?: string
 *  - bookingId?: string
 *  - compact?: bool   (smaller styling for nesting in tight layouts)
 */
const SubsequentAdjustmentsPanel = ({ receiptId, bookingId, compact = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const url = receiptId
          ? `${API}/credit-notes/by-receipt/${receiptId}`
          : bookingId
          ? `${API}/credit-notes/by-booking/${bookingId}`
          : null;
        if (!url) { setItems([]); return; }
        const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setItems(r.data?.items || []);
      } catch (e) {
        setItems([]);
      } finally { setLoading(false); }
    };
    load();
  }, [receiptId, bookingId]);

  if (loading || items.length === 0) return null;
  const total = items.reduce((s, it) => s + (it.amount || 0), 0);

  const openPdf = (id) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/credit-notes/${id}/pdf?token=${encodeURIComponent(token)}`, '_blank');
  };

  return (
    <div
      className={`mt-2 rounded-md border-2 border-amber-300 bg-amber-50 ${compact ? 'p-2' : 'p-3'}`}
      dir="rtl"
      data-testid="subsequent-adjustments-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-amber-800 text-sm flex items-center gap-1">
          📎 التعديلات اللاحقة ({items.length})
        </div>
        <div className="text-sm font-bold text-amber-700">
          {fmt(total)} دج
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map(cr => (
          <div key={cr.id} className="bg-white rounded p-2 text-[12px] border border-amber-200 flex justify-between items-center" data-testid={`adj-row-${cr.id}`}>
            <div className="space-y-0.5">
              <div className="font-mono font-bold text-amber-800">💸 {cr.credit_note_number}</div>
              <div className="text-gray-700">{cr.reason}</div>
              <div className="text-[10px] text-gray-500">
                {new Date(cr.created_at).toLocaleString('ar-DZ')}
                {cr.deduction_source === 'agency_balance' ? ' • 🏢 من رصيد الوكالة' : ' • 🏦 من الخزينة'}
                {cr.created_by_name ? ` • ${cr.created_by_name}` : ''}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="font-bold text-amber-700">{fmt(cr.amount)} دج</div>
              <button
                type="button"
                onClick={() => openPdf(cr.id)}
                className="text-[10px] text-amber-700 hover:underline"
              >🖨️ PDF</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubsequentAdjustmentsPanel;
